import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import {
  ERROR_ALERT_SEARCH_FIELDS,
  type ErrorAlertSearchFieldId,
  type SupportErrorAlertSearchFilter,
} from "src/interfaces/supportErrorAlerts.interface";

export type ErrorAlertsDatePreset = "day" | "week" | "month" | "custom";

export interface ErrorAlertsUrlState {
  preset: ErrorAlertsDatePreset;
  from: Date;
  to: Date;
  filters: SupportErrorAlertSearchFilter[];
  alertId: string | null;
}

export const ERROR_ALERTS_BASE_PATH = "/support/error-alerts";
export const ErrorAlertsDefaultPath = `${ERROR_ALERTS_BASE_PATH}/day`;

const VALID_PRESETS = new Set<ErrorAlertsDatePreset>(["day", "week", "month"]);

const CUSTOM_TIME_WINDOW = /^(\d{4}-\d{2}-\d{2})-(\d{4}-\d{2}-\d{2})$/;

const VALID_FILTER_FIELDS = new Set<ErrorAlertSearchFieldId>(
  ERROR_ALERT_SEARCH_FIELDS.map((field) => field.id)
);

export function getErrorAlertsPresetRange(preset: ErrorAlertsDatePreset) {
  const now = new Date();
  if (preset === "day") return { from: startOfDay(now), to: endOfDay(now) };
  if (preset === "week") return { from: startOfWeek(now), to: endOfWeek(now) };
  if (preset === "month")
    return { from: startOfMonth(now), to: endOfMonth(now) };
  return { from: startOfDay(now), to: endOfDay(now) };
}

function formatCustomTimeWindow(from: Date, to: Date) {
  return `${format(from, "yyyy-MM-dd")}-${format(to, "yyyy-MM-dd")}`;
}

export function getAlertDayKey(createdAt: string | null) {
  if (createdAt && /^\d{4}-\d{2}-\d{2}/.test(createdAt)) {
    return createdAt.slice(0, 10);
  }
  return format(new Date(), "yyyy-MM-dd");
}

export function buildErrorAlertSharePath(
  alertId: string,
  createdAt: string | null
) {
  const day = getAlertDayKey(createdAt);
  return `${ERROR_ALERTS_BASE_PATH}/${day}-${day}/${encodeURIComponent(
    alertId
  )}`;
}

export function buildErrorAlertShareUrl(
  alertId: string,
  createdAt: string | null,
  origin = window.location.origin
) {
  return `${origin}${buildErrorAlertSharePath(alertId, createdAt)}`;
}

function parseFilters(search: string) {
  const raw = new URLSearchParams(search).get("filters");
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((entry) => ({
        field: typeof entry?.field === "string" ? entry.field.trim() : "",
        value: typeof entry?.value === "string" ? entry.value.trim() : "",
      }))
      .filter(
        (entry): entry is SupportErrorAlertSearchFilter =>
          VALID_FILTER_FIELDS.has(entry.field as ErrorAlertSearchFieldId) &&
          entry.value.length > 0
      );
  } catch {
    return [];
  }
}

function parsePathSegments(pathname: string) {
  const prefix = ERROR_ALERTS_BASE_PATH;
  const rest = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length).replace(/^\//, "")
    : "";
  const segments = rest.split("/").filter(Boolean);

  return {
    timeWindow: segments[0] || "day",
    alertId: segments[1] ? decodeURIComponent(segments[1]) : null,
  };
}

function parseTimeWindow(
  timeWindow: string
): Pick<ErrorAlertsUrlState, "preset" | "from" | "to"> {
  if (VALID_PRESETS.has(timeWindow as ErrorAlertsDatePreset)) {
    const preset = timeWindow as ErrorAlertsDatePreset;
    return {
      preset,
      ...getErrorAlertsPresetRange(preset),
    };
  }

  const match = timeWindow.match(CUSTOM_TIME_WINDOW);
  if (match) {
    const from = startOfDay(parseISO(match[1]));
    const to = endOfDay(parseISO(match[2]));
    if (isValid(from) && isValid(to) && from.getTime() <= to.getTime()) {
      return { preset: "custom", from, to };
    }
  }

  return {
    preset: "day",
    ...getErrorAlertsPresetRange("day"),
  };
}

export function parseErrorAlertsUrlState(
  pathname: string,
  search = ""
): ErrorAlertsUrlState {
  const { timeWindow, alertId } = parsePathSegments(pathname);
  const { preset, from, to } = parseTimeWindow(timeWindow);

  return {
    preset,
    from,
    to,
    filters: parseFilters(search),
    alertId,
  };
}

export function mergeErrorAlertsUrlState(
  current: ErrorAlertsUrlState,
  patch: Partial<ErrorAlertsUrlState>
): ErrorAlertsUrlState {
  return {
    preset: patch.preset ?? current.preset,
    from: patch.from ?? current.from,
    to: patch.to ?? current.to,
    filters: patch.filters ?? current.filters,
    alertId: patch.alertId !== undefined ? patch.alertId : current.alertId,
  };
}

export function buildErrorAlertsPath(state: ErrorAlertsUrlState) {
  const timeWindow =
    state.preset === "custom"
      ? formatCustomTimeWindow(state.from, state.to)
      : state.preset;

  let pathname = `${ERROR_ALERTS_BASE_PATH}/${timeWindow}`;
  if (state.alertId) {
    pathname += `/${encodeURIComponent(state.alertId)}`;
  }

  const params = new URLSearchParams();
  if (state.filters.length > 0) {
    params.set("filters", JSON.stringify(state.filters));
  }

  const serialized = params.toString();
  return {
    pathname,
    search: serialized ? `?${serialized}` : "",
  };
}

export function serializeErrorAlertsLocation(state: ErrorAlertsUrlState) {
  const { pathname, search } = buildErrorAlertsPath(state);
  return `${pathname}${search}`;
}
